import { useEffect, useState } from 'react';
import { usePageTitle } from './PageTitleContext';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import DateTimeNow from '../components/DateTimeNow';
import Cart from '../components/Cart/Cart';
import Bell from '../components/Bell/Bell';
import Assistant from '../components/Assistant/Assistant';
import VirtualKeyboard from '../components/VirtualKeyboard/VirtualKeyboard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faBars } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthProvider';
import { eventBus } from '../bus/eventBus';
import { initOfflineSync } from '../offline/syncService';
import OfflineStatusBadge from '../components/OfflineStatusBadge';
import useTablerIconsReady from '../hooks/useTablerIconsReady';
import './MainLayout.scss';

export default function MainLayout() {
    const { title, titleIcon, titleAvatar, titleBadge, titleDescription } = usePageTitle();
    const navigate = useNavigate();
    const location = useLocation();
    const { settings } = useAuth();
    // collapses the title bar while a listing table below is scrolled down, to free up
    // vertical space — Table.js reports scroll direction over the event bus since the
    // scrollable element lives deep inside the routed page, not here
    const [headerHidden, setHeaderHidden] = useState(false);

    useTablerIconsReady();
    useEffect(() => initOfflineSync(), []);

    useEffect(() => {
        const handler = (visible) => setHeaderHidden(!visible);
        eventBus.on('listingChromeVisibility', handler);
        return () => eventBus.off('listingChromeVisibility', handler);
    }, []);

    useEffect(() => { setHeaderHidden(false); }, [location.pathname]);

    const clockVal = settings?.['general_general_settings_show_clock'];
    const showClock = clockVal === undefined || clockVal === true || clockVal === 'true';

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    return (
        <>
        {/* --vk-height is set by VirtualKeyboard while it's open, so the app shrinks above it */}
        <div style={{ display: 'flex', height: 'calc(100vh - var(--vk-height, 0px))', overflow: 'hidden' }}>
            {/* Sidebar */}
            <div style={{ flexShrink: 0, height: '100%' }}>
                <Sidebar />
            </div>

            {/* Zone centrale */}
            <div style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden'
            }}>

                {/* Header — fixed height so its bottom border lines up with the
                    sidebar's own 64px brand row (see Sidebar.scss .sidebar-header);
                    a min/max-height range let a two-line title (see Products.js)
                    grow the box taller than that and break the alignment. */}
                <div style={{
                    flexShrink: 0,
                    height: headerHidden ? 0 : 64,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: headerHidden ? '0 20px' : '10px 20px',
                    background: '#fff',
                    borderBottom: headerHidden ? 'none' : '1px solid #f0f0f0',
                    gap: 12,
                    overflow: 'hidden',
                    opacity: headerHidden ? 0 : 1,
                    transition: 'height 0.25s ease, opacity 0.2s ease, padding 0.25s ease',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '1 1 auto', minWidth: 0 }}>
                        <button
                            className="mobile-sidebar-toggle"
                            onClick={() => eventBus.emit('toggleMobileSidebar')}
                            aria-label="Open menu"
                        >
                            <FontAwesomeIcon icon={faBars} />
                        </button>
                        {/* no "back" from the home page */}
                        {location.pathname !== '/dashboard' && <button
                            className="header-back-btn"
                            onClick={() => navigate(-1)}
                            style={{
                                border: 'none',
                                background: '#f0f0f0',
                                cursor: 'pointer',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                transition: 'background-color 0.2s ease',
                            }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#dedede'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                            aria-label="Go back"
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </button>}
                        {titleAvatar ? (
                            <span className="header-title-icon">{titleAvatar}</span>
                        ) : titleIcon && (
                            <span
                                className="header-title-icon"
                                style={{
                                    width: 35, height: 35, borderRadius: '50%', flexShrink: 0,
                                    background: '#f0f0f0', color: '#111827',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 16,
                                }}
                            >
                                <i className={`ti ${titleIcon}`} />
                            </span>
                        )}
                        <div style={{ minWidth: 0, overflow: 'hidden' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                <h3
                                    style={{ fontSize: '15px', fontWeight: 700, margin: 0, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    dangerouslySetInnerHTML={{ __html: title }}
                                />
                                {titleBadge}
                            </div>
                            {titleDescription && (
                                <div style={{ fontSize: 12, fontWeight: 400, color: '#9ca3af', marginTop: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {titleDescription}
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                        <OfflineStatusBadge />
                        {showClock && <span className="header-clock">
                            <DateTimeNow />
                        </span>}
                        <Assistant />
                        <Bell />
                        <Cart />
                    </div>
                </div>

                {/* Contenu central */}
                <div style={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden'
                }}>
                    <Outlet />
                </div>

            </div>
        </div>
        <VirtualKeyboard />
        </>
    );
}
